import React from "react";
import HtmlParser, { domToReact, attributesToProps } from "html-react-parser";
import _, { isArray } from "lodash";
import parse from "style-to-js";

/**
 * @description Separate data tokens as standard tokens and repeating adapter
 * @param adapters - All preview data
 */
const separatePreviewDataTokens = (adapters) => {
  try {
    if (isArray(adapters) && adapters.length > 0) {
      const standardAdapters = adapters.find((adapter) => !adapter.isRepeating);
      const repeatingAdapters = adapters.filter(
        (adapter) => adapter.isRepeating
      );
      return {
        standardTokens: standardAdapters.dataTokens[0],
        repeatingAdapters,
      };
    } else {
      return {
        standardTokens: [],
        repeatingAdapters: [],
      };
    }
  } catch (error) {
    return {
      standardTokens: [],
      repeatingAdapters: [],
    };
  }
};

/**
 * @description Separate data tokens as standard tokens and repeating adapter
 * @param adapters - All preview data
 * @param id - Repeating adapter id
 */
const getRepeatingTokensById = (adapters, id) => {
  try {
    if (isArray(adapters) && adapters.length > 0 && id) {
      const repeatingAdapter = adapters.find(
        (adapter) => adapter.adapterId == id
      );
      return repeatingAdapter.dataTokens;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description - Generate preview options
 * @param defaultOptions - default options for the parser
 * @param adapterData - Adapter data tp bind
 */
const getPreviewOptions = (defaultOptions, adapters) => {
  try {
    const { standardTokens, repeatingAdapters } =
      separatePreviewDataTokens(adapters);
    const options = {
      trim: true,
      replace: (domNode) => {
        if (
          domNode.name &&
          domNode.name === "div" &&
          domNode.attribs &&
          domNode.attribs.hasOwnProperty("sectionname") &&
          !domNode.attribs.hasOwnProperty("hidden")
        ) {
          return (
            <div style={{ ...parse(domNode.attribs?.style) }}>
              {domToReact(domNode.children, {
                replace: (domNode) => {
                  if (
                    domNode.name &&
                    domNode.name === "span" &&
                    domNode.attribs &&
                    (domNode.attribs?.class ==
                      "react-resizable-handle react-resizable-handle-se" ||
                      domNode.attribs?.class ==
                        "react-resizable-handle react-resizable-handle-nw" ||
                      domNode.attribs?.class ==
                        "react-resizable-handle react-resizable-handle-ne" ||
                      domNode.attribs?.class ==
                        "react-resizable-handle react-resizable-handle-sw")
                  ) {
                    return <></>;
                  }
                  //lock-indicator
                  if (
                    domNode.name &&
                    domNode.name === "div" &&
                    domNode.attribs &&
                    domNode.attribs?.class == "lock-indicator"
                  ) {
                    return <></>;
                  }
                  if (
                    domNode.name &&
                    domNode.name === "div" &&
                    domNode.attribs &&
                    domNode.attribs?.class &&
                    domNode.attribs?.class?.includes("usc-section-content")
                  ) {
                    return (
                      <div class="usc-section-content">
                        {domToReact(domNode.children, {
                          replace: (domNode) => {
                            if (
                              domNode.name &&
                              domNode.name === "tr" &&
                              domNode.attribs &&
                              domNode.attribs.id === "repeatingcontent" //repeatingadapterid
                            ) {
                              return (
                                repeatingAdapters.length > 0 && (
                                  <>
                                    {getRepeatingTokensById(
                                      repeatingAdapters,
                                      domNode.attribs.repeatingadapterid
                                    ).map((tokens, index) => (
                                      <tr key={index.toString()}>
                                        {domToReact(domNode.children, {
                                          replace: (domNode) => {
                                            if (
                                              domNode.name &&
                                              domNode.name === "params"
                                            ) {
                                              return getPreviewElement(
                                                tokens,
                                                domNode.children,
                                                defaultOptions,
                                                domNode.attribs
                                              );
                                            }
                                          },
                                        })}
                                      </tr>
                                    ))}
                                  </>
                                )
                              );
                            }
                            if (domNode.name && domNode.name === "params") {
                              return getPreviewElement(
                                standardTokens,
                                domNode.children,
                                defaultOptions,
                                domNode.attribs
                              );
                            }
                          },
                        })}
                      </div>
                    );
                  }
                },
              })}
            </div>
          );
        }
      },
    };
    return options;
  } catch (error) {
    return null;
  }
};

/**
 * @description - Create React element(s) for the preview content from the html content
 * @param pageIndex - Page index
 * @param defaultOptions - Options for the parser
 * @param adapterData - Adapter data to bind
 */
export const getPreviewHtml = (pageIndex, defaultOptions, adapters) => {
  try {
    const page = document.querySelector(`[page=page_${pageIndex}]`);
    const options = getPreviewOptions(defaultOptions, adapters);
    if (page && options) {
      return HtmlParser(
        document.querySelector(`[page=page_${pageIndex}]`).outerHTML,
        options
      );
    } else {
      // here return error content show preview error
      return <></>;
    }
  } catch (error) {
    console.error("getPreviewHtml => ", error);
    // here return error content show preview error
    return <></>;
  }
};

/**
 * @description - Get value for the token
 * @param tokens - Data tokens
 * @param domeNodeChildren - Children of the current element
 * @param options - Options for the Html Parser
 */
const getTokenDataValue = (tokens, domeNodeChildren, options) => {
  try {
    if (isArray(tokens) && tokens.length > 0) {
      const tokenName = domToReact(domeNodeChildren, options)
        ?.toString()
        ?.trim();
      if (tokenName) {
        const token = _.find(tokens, { tokenName: tokenName });
        if (token) {
          return token.tokenDataValue;
        } else {
          return "";
        }
      } else {
        return "";
      }
    } else {
      return "";
    }
  } catch (error) {
    console.error("getTokenDataValue function error => ", error);
    return "";
  }
};

/**
 * @description - Generate preview html element with token data
 * @param tokens - Data tokens
 * @param domeNodeChildren - Children of the current element
 * @param options - Options for the Html Parser
 * @param attributes - Attributes of the current dom node element
 */
const getPreviewElement = (tokens, domeNodeChildren, options, attributes) => {
  try {
    // generate props for tag
    const props = attributesToProps(attributes);
    const tokenDataValue = getTokenDataValue(tokens, domeNodeChildren, options);
    if (tokenDataValue) {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = tokenDataValue;
      const imgElement = tempElement.getElementsByTagName("img")[0];
      if (imgElement) {
        return (
          <img
            {...props}
            src={imgElement.src}
            alt={imgElement.alt}
            width={imgElement.width}
            height={imgElement.height}
            style={{
              margin: imgElement.style.margin,
              padding: imgElement.style.padding,
              marginBottom: imgElement.style.marginBottom,
              marginTop: imgElement.style.marginTop,
              marginLeft: imgElement.style.marginLeft,
              marginRight: imgElement.style.marginRight,
              paddingLeft: imgElement.style.paddingLeft,
              paddingRight: imgElement.style.paddingRight,
              paddingTop: imgElement.style.paddingTop,
              paddingBottom: imgElement.style.paddingBottom,
            }}
          />
        );
      } else {
        return <span {...props}>{HtmlParser(tokenDataValue)}</span>;
      }
    } else {
      if (isArray(tokens) && tokens.length > 0) {
        const isRepeating = domeNodeChildren[0].parent.attribs.isrepeating;
        return (
          <>
            {isRepeating ? (
              <span {...props}>
                [[--{domToReact(domeNodeChildren, options)}--]]
              </span>
            ) : (
              <span {...props}>
                [[{domToReact(domeNodeChildren, options)}]]
              </span>
            )}
          </>
        );
      } else {
        const isRepeating = domeNodeChildren[0].parent.attribs.isrepeating;
        return (
          <>
            {isRepeating ? (
              <span {...props}>
                [[--{domToReact(domeNodeChildren, options)}--]]
              </span>
            ) : (
              <span {...props}>
                [[{domToReact(domeNodeChildren, options)}]]
              </span>
            )}
          </>
        );
      }
    }
  } catch (error) {
    console.log("error => ", error);
    return <span></span>;
  }
};
